import React, { useState } from "react";
import { motion } from "framer-motion";
import tw from "twin.macro";
import styled from "styled-components";
import { SectionHeading, Subheading as SubheadingBase } from "components/misc/Headings.js";
import { ReactComponent as PlusIcon } from "feather-icons/dist/icons/plus.svg";
import { ReactComponent as MinusIcon } from "feather-icons/dist/icons/minus.svg";

// Layout Wrappers
const Container = tw.div`relative font-[CustomFont]`;
const Content = tw.div`max-w-screen-xl mx-auto py-16 lg:py-20`;
const TwoColumn = tw.div`flex flex-col lg:flex-row items-center`;
const Column = tw.div`w-full lg:w-1/2`;

// Image Box
const Image = styled.div(props => [
  `background-image: url("${props.imageSrc}");`,
  props.imageContain 
    ? tw`bg-contain bg-no-repeat` 
    : tw`bg-no-repeat`,
  props.imageShadow ? tw`shadow` : tw`shadow-none`,
  tw`hidden lg:block rounded h-144`,
  !props.imageContain && `
    background-size: 100% 100%; /* full width, height auto */
    background-position: top center; /* top aligned vertically, centered horizontally */
  `
]);

// Text Elements
const FAQContent = styled.div`
  ${tw`lg:ml-12`}
  direction: rtl;
  text-align: right;
`;

const Subheading = tw(SubheadingBase)`mb-4 text-center lg:text-right`;

const Heading = styled(SectionHeading)`
  ${tw`lg:text-right`}
  font-family: 'CustomFont', sans-serif;
  color: #C78A41;
`;

const Description = styled.p`
  ${tw`max-w-xl text-center mx-auto lg:mx-0 lg:text-right lg:max-w-none leading-relaxed mt-4`}
  font-size: 1.125rem;
  font-family: 'CustomFont', sans-serif;
  font-weight: 500;
`;

// FAQ Styles
const FAQSContainer = tw.dl`mt-12`;
const FAQ = tw.div`cursor-pointer mt-8 select-none border px-8 py-4 rounded-lg`;
const Question = styled.dt`
  ${tw`flex justify-between items-center`}
  direction: rtl;
`;

const QuestionText = styled.span`
  ${tw`text-lg lg:text-xl font-semibold`}
  color: #0f3d1e;
  font-family: 'CustomFont', sans-serif;
`;

const QuestionToggleIcon = styled.span`
  background-color: #C78A41;
  ${tw`ml-2 text-white p-1 rounded-full transition duration-300`}
  svg {
    ${tw`w-4 h-4`}
  }
`;

const Answer = motion(styled.dd`
  font-size: 1rem;
  line-height: 1.6;
  font-family: 'CustomFont', sans-serif;
  direction: rtl;
  text-align: right;
`);

export default ({
  subheading = "",
  heading = "مستقبل کے عزائم",
  description = "",
  imageSrc = process.env.PUBLIC_URL + "/logo512.png",
  imageContain = false,
  imageShadow = true,
  faqs = null
}) => {
  const defaultFaqs = [
    { question: "1. اساتذہ کے لیے رہائش گاہ کی تعمیر", answer: "• اساتذہ کی سہولت اور سکونت کے لیے جامعہ میں رہائش گاہوں کی تعمیر۔" },
    { question: "2. جامعہ کے اطراف حصار بندی", answer: "• جامعہ کی حدود کو محفوظ بنانے کے لیے حصار بندی کا اہتمام۔" },
    { question: "3. باب الداخلہ (مین گیٹ) کی تعمیر", answer: "• جامعہ کے وقار میں اضافہ کے لیے خوبصورت اور مضبوط مین گیٹ کی تعمیر۔" },
    { question: "4. جنرل لائبریری کے لیے مستقل عمارت", answer: "• طلبہ اور اساتذہ کو علمی مواد کی فراہمی کے لیے مستقل لائبریری کی تعمیر۔" },
    { question: "5. ملک اور بیرون ملک سے نادر کتب کی فراہمی", answer: "• لائبریری کو مزید مضبوط کرنے کے لیے نادر و مفید کتب کا حصول۔" },
    { question: "6. مسجد جامعہ اور طلبہ کے لیے رہائش گاہوں کی تعمیر", answer: "• جامعہ کی مسجد کی تعمیر اور طلبہ کے لیے بہتر رہائش گاہوں کا انتظام۔" },
    { question: "7. قاعة المحاضرات اور مہمان خانہ کی تعمیر", answer: "• علمی و تعلیمی پروگرامز کے لیے لیکچر ہال اور مہمانوں کی رہائش۔" },
    { question: "8. دیگر تعلیمی شعبہ جات کا قیام", answer: "• علمی تخصصات مثلا شریعت و حدیث کے لیے ماہر علماء کی فراہمی۔" }
  ];

  if (!faqs || faqs.length === 0) faqs = defaultFaqs;

  const [activeQuestionIndex, setActiveQuestionIndex] = useState(null);

  const toggleQuestion = index => {
    setActiveQuestionIndex(activeQuestionIndex === index ? null : index);
  };

  return (
    <Container>
      <Content>
        <TwoColumn>
          <Column>
            <Image
              imageContain={imageContain}
              imageShadow={imageShadow}
              imageSrc={imageSrc}
            />
          </Column>
          <Column>
            <FAQContent>
              {subheading && <Subheading>{subheading}</Subheading>}
              <Heading>{heading}</Heading>
              <Description>{description}</Description>
              <FAQSContainer>
                {faqs.map((faq, index) => (
                  <FAQ key={index} onClick={() => toggleQuestion(index)} className="group">
                    <Question>
                      <QuestionText>{faq.question}</QuestionText>
                      <QuestionToggleIcon>
                        {activeQuestionIndex === index ? <MinusIcon /> : <PlusIcon />}
                      </QuestionToggleIcon>
                    </Question>
                    <Answer
                      variants={{
                        open: { opacity: 1, height: "auto", marginTop: "16px" },
                        collapsed: { opacity: 0, height: 0, marginTop: "0px" }
                      }}
                      initial="collapsed"
                      animate={activeQuestionIndex === index ? "open" : "collapsed"}
                      transition={{ duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] }}
                    >
                      {faq.answer}
                    </Answer>
                  </FAQ>
                ))}
              </FAQSContainer>
            </FAQContent>
          </Column>
        </TwoColumn>
      </Content>
    </Container>
  );
};
